import React, { useId } from 'react';
import { Logo } from '@/components/ui/Logo';
import { Link } from 'react-router-dom';
import { cx } from '@/components/growth-desk/ui';

/**
 * The sign-in frame (auth board): navy page, logo, a big line on the left, the form in a white
 * card on the right, and the orange ring. Every auth page (sign in, create account, verify,
 * forgot, reset) uses it.
 */
export const AuthShell: React.FC<{ headline: React.ReactNode; sub?: React.ReactNode; children: React.ReactNode; wideCard?: boolean }> = ({ headline, sub, children, wideCard }) => (
  <div className="relative min-h-[100dvh] overflow-hidden bg-[#0F1526] text-white">
    {/* The orange ring */}
    <span aria-hidden="true" className="pointer-events-none absolute -bottom-[420px] -left-[240px] h-[640px] w-[640px] rounded-full border-[120px] border-[#F27C28]" />
    <div className="relative mx-auto flex min-h-[100dvh] max-w-[1200px] flex-col px-5 pb-12 pt-7 md:px-10">
      <Logo onDark height={30} className="self-start" />
      <div className="grid flex-1 items-center gap-10 py-10 lg:grid-cols-[1fr_440px] lg:gap-16">
        <div className="flex flex-col gap-4">
          <h1 className="m-0 font-display text-[44px] font-extrabold leading-[0.98] tracking-[-0.045em] md:text-[72px]">{headline}</h1>
          {sub && <p className="m-0 max-w-[480px] text-[17px] leading-relaxed text-[#C9CEDB]">{sub}</p>}
        </div>
        <div className={cx('w-full rounded-[28px] bg-white p-7 text-[#0F1526] shadow-2xl md:p-8', wideCard && 'lg:w-[480px]')}>
          {children}
        </div>
      </div>
      <nav aria-label="Legal" className="relative flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#9AA3B8] lg:justify-end">
        <Link to="/terms" className="text-inherit no-underline hover:text-white">Terms</Link>
        <Link to="/privacy" className="text-inherit no-underline hover:text-white">Privacy</Link>
        <Link to="/cookie-policy" className="text-inherit no-underline hover:text-white">Cookies</Link>
      </nav>
    </div>
  </div>
);

/** The card's title. */
export const AuthTitle: React.FC<{ children: React.ReactNode; sub?: React.ReactNode }> = ({ children, sub }) => (
  <div className="mb-5 flex flex-col gap-1">
    <h2 className="m-0 font-display text-[28px] font-extrabold tracking-[-0.03em] text-[#0F1526]">{children}</h2>
    {sub && <p className="m-0 text-sm leading-relaxed text-[#4A5468]">{sub}</p>}
  </div>
);

/** A labelled input on the white card; the error shows under it and rings it red. */
export const AuthField: React.FC<Omit<React.InputHTMLAttributes<HTMLInputElement>, 'id'> & { label: React.ReactNode; error?: React.ReactNode; aside?: React.ReactNode; hint?: React.ReactNode }> = ({ label, error, aside, hint, className, ...rest }) => {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm text-[#4A5468]">{label}</label>
        {aside}
      </div>
      <input id={id} aria-invalid={!!error || undefined} aria-describedby={error ? `${id}-err` : undefined}
        className={cx('min-h-[52px] w-full rounded-[12px] border-0 bg-[#F1F2F6] px-4 text-[16px] text-[#0F1526] placeholder:text-[#8A92A6] focus:outline-none focus:ring-2 focus:ring-[#0F1526]',
          error && 'ring-2 ring-[#B42318] focus:ring-[#B42318]', className)} {...rest} />
      {error ? <p id={`${id}-err`} className="m-0 text-sm font-semibold text-[#B42318]">{error}</p> : hint ? <p className="m-0 text-xs text-[#646D80]">{hint}</p> : null}
    </div>
  );
};

/** The orange submit button. */
export const AuthSubmit: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }> = ({ loading, children, disabled, className, ...rest }) => (
  <button type="submit" disabled={disabled || loading} aria-busy={loading || undefined}
    className={cx('flex min-h-[52px] w-full items-center justify-center gap-2 rounded-[12px] bg-[#F27C28] font-display text-[16px] font-bold text-[#0F1526] transition-colors hover:bg-[#E06A16] disabled:opacity-60', className)} {...rest}>
    {loading && <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#0F1526] border-t-transparent" aria-hidden="true" />}
    {children}
  </button>
);

/** The quiet outline button on the card. */
export const AuthGhost: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, className, type = 'button', ...rest }) => (
  <button type={type} className={cx('flex min-h-[52px] w-full items-center justify-center rounded-[12px] bg-white font-display text-[16px] font-bold text-[#0F1526] shadow-[inset_0_0_0_1.5px_#D5D8E0] transition-shadow hover:shadow-[inset_0_0_0_1.5px_#0F1526] disabled:opacity-60', className)} {...rest}>
    {children}
  </button>
);

/** A notice inside the card. */
export const AuthNote: React.FC<{ tone?: 'bad' | 'ok' | 'info'; children: React.ReactNode }> = ({ tone = 'bad', children }) => (
  <div role={tone === 'bad' ? 'alert' : 'status'} className={cx('rounded-[12px] px-4 py-3 text-sm leading-relaxed',
    tone === 'bad' ? 'bg-[#FDECEA] text-[#8F1D14]' : tone === 'ok' ? 'bg-[#E7F4EC] text-[#14603F]' : 'bg-[#EEF0F6] text-[#2B3346]')}>
    {children}
  </div>
);

export const authLink = 'font-semibold text-[#B85A12] underline underline-offset-2 hover:text-[#8A4B00]';

export default AuthShell;
